import { api, isApiError } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'

import {
  publicEventDetailDtoSchema,
  publicScheduleDtoSchema,
  venueShareLinkStateDtoSchema,
  type PublicEventDetailDto,
  type PublicScheduleDto,
  type VenueShareLinkStateDto,
} from '@lib/dto/public-schedule.dto'
import type { IPublicScheduleService } from '@/types/PublicScheduleService.types'

/**
 * The venue's public schedule, from both ends. See `docs/public-schedule.md`.
 *
 * The two public reads pass `anonymous: true`: their routes have no auth guard,
 * so a signed-in manager checking the crew's view would otherwise send a live
 * access token to a route with no use for it.
 *
 * Nothing here logs the token. It is the whole of the secret, and a breadcrumb
 * carrying it would put a working link into the log of every failure.
 */
/**
 * A 404 from a public read is the page's content, not a fault.
 *
 * `isExpectedRefusal` keeps 404 an error everywhere else, and is right to: in
 * the signed-in app a link to nothing is nearly always a bug. Here it is the
 * ordinary life of a permalink — a bookmark kept after the link was replaced, an
 * event that has fallen out of the two-week window — and the server has already
 * written the sentence the reader gets. Filing an issue each time somebody opens
 * a stale bookmark would bury the failures that mean something.
 */
function isPublicMiss<E>(error: E): boolean {
  return isApiError(error) && error.status === 404
}
class PublicScheduleService implements IPublicScheduleService {
  async shareLink(venueId: string): Promise<VenueShareLinkStateDto> {
    try {
      return await api.get(`/venues/${venueId}/share-link`, venueShareLinkStateDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load the venue share link', { venueId }, error)
      throw error
    }
  }

  async replaceShareLink(venueId: string): Promise<VenueShareLinkStateDto> {
    try {
      return await api.post(`/venues/${venueId}/share-link`, {}, venueShareLinkStateDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to replace the venue share link', { venueId }, error)
      throw error
    }
  }

  async schedule(token: string): Promise<PublicScheduleDto> {
    try {
      return await api.get(`/public/schedule/${token}`, publicScheduleDtoSchema, { anonymous: true })
    } catch (error) {
      if (!isPublicMiss(error)) logRequestFailure('Failed to load a public schedule', {}, error)
      throw error
    }
  }

  async event(token: string, eventId: string): Promise<PublicEventDetailDto> {
    try {
      return await api.get(`/public/schedule/${token}/events/${eventId}`, publicEventDetailDtoSchema, {
        anonymous: true,
      })
    } catch (error) {
      if (!isPublicMiss(error)) logRequestFailure('Failed to load a public event', { eventId }, error)
      throw error
    }
  }
}

export const publicScheduleService = new PublicScheduleService()
